import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";

import ClearIcon from "@mui/icons-material/Clear";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { DatabaseContext, UserContext } from "../../context";
import firebase from "../../firebase";
import { GLOBAL_ROLE } from "../admin/fileVisibility";
import GroupMembersDialog from "../GroupMembersDialog";
import UserAvatar from "../management/UserAvatar";
import NoPermissionsMessage from "../NoPermissionsMessage";
import ToolbarWithLogo from "../ToolbarWithLogo";
import VirtualizedList from "../VirtualizedList";
import {
    primarySurface,
    primaryTintBorder,
    warningBorder,
    warningSurface,
} from "../../surfaceTints";
import NodiantCalendar from "../calendar/NodiantCalendar";
import DesktopAppPageFrame from "./DesktopAppPageFrame";
import HubGroupNavigator, { GroupRow } from "./HubGroupNavigator";
import {
    GRACE_DAYS,
    activationLabel,
    addDaysIso,
    buildHubTree,
    describeGrantResult,
    formatLicenceDate,
    hasKeyForCurrentPeriod,
    latestLicenceByUid,
    licenceIsCurrent,
    licenceStatus,
    macsLabel,
    renewalCandidates,
    resolveRecipients,
    todayIsoDate,
} from "./desktopAccess";

const ROW_HEIGHT = 64;
const GROUP_ROW_HEIGHT = 72;
// The lists fill the window below the app bar and the page's header, banner
// and tab row, and above its footer and the bottom toolbar (measured at 470px
// in all on a desktop-width window), but never shrink below a usable height.
const LIST_HEIGHT = "max(360px, calc(100vh - 470px))";

// VirtualizedList leaves scrolling to its caller
const listSx = { height: LIST_HEIGHT, overflowY: "auto" };

// grantDesktopAccess takes at most 500 people per call
const GRANT_BATCH_SIZE = 500;

const STATUS_ORDER = { active: 0, grace: 1, expired: 2 };

// A valid key's badge says how much it has been used; once it has expired the
// badge says that instead (and the row's second line keeps the activation)
function statusChipProps(status, licence) {
    if (status.status === "active") {
        const label = activationLabel(licence);
        return label === "Not activated yet"
            ? { label: label, color: "default", variant: "outlined" }
            : { label: label, color: "success", variant: "outlined" };
    }
    if (status.status === "grace") {
        return {
            label:
                "Grace period · " +
                status.daysLeft +
                (status.daysLeft === 1 ? " day left" : " days left"),
            color: "warning",
            variant: "outlined",
        };
    }
    return { label: "Key expired", color: "error", variant: "filled" };
}

// The calendar deals in Date objects; licence dates are plain "yyyy-mm-dd"
function toLocalDate(isoDate) {
    if (!isoDate) return null;
    const [year, month, day] = isoDate.split("-").map(Number);
    return new Date(year, month - 1, day);
}

function toIsoDate(date) {
    if (!date) return "";
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
}

function matchesFilter(filter, ...values) {
    if (!filter) return true;
    const needle = filter.toLowerCase();
    return values.some((value) => (value || "").toLowerCase().includes(needle));
}

// System Digital Managers give people access to Nodiant Coder. The server
// decides the organisation (FAW) and the expiry date; this page only chooses
// who, and never sees a key.
export default function DesktopAccessPage() {
    const history = useHistory();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();

    const { currentUserData } = useContext(UserContext);
    const { users, usersLoaded, groups, folders } = useContext(DatabaseContext);

    const isSystemManager = currentUserData
        ? currentUserData.role === GLOBAL_ROLE
        : undefined;

    const [overview, setOverview] = useState(null);
    const [overviewError, setOverviewError] = useState(null);
    const [tab, setTab] = useState("grant");
    const [mode, setMode] = useState("people");
    const [filter, setFilter] = useState("");
    const [selectedUsers, setSelectedUsers] = useState(() => new Set());
    const [selectedGroups, setSelectedGroups] = useState(() => new Set());
    // { kind: "grant" | "renew", uids, alreadyHaveAccess, maxDevices, expiryDate }
    const [pendingGrant, setPendingGrant] = useState(null);
    // Macs per person for the next grant, 1 up to Matthew's limit
    const [grantMacs, setGrantMacs] = useState(1);
    // When the next grant's keys expire: the limit unless shortened here
    const [grantExpiry, setGrantExpiry] = useState("");
    // The Expires button, while its calendar is open
    const [expiryAnchor, setExpiryAnchor] = useState(null);
    // { row, maxDevices } while the Change Macs dialog is open
    const [macsEdit, setMacsEdit] = useState(null);
    const [savingMacs, setSavingMacs] = useState(false);
    // The Access list row being removed, while its confirmation is open
    const [removeTarget, setRemoveTarget] = useState(null);
    const [removing, setRemoving] = useState(false);
    const [granting, setGranting] = useState(false);
    // The group whose member list is open
    const [viewingGroup, setViewingGroup] = useState(null);

    useEffect(() => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
            if (!user) {
                history.replace("/signin?url=" + location.pathname);
            }
        });
        return () => unsubscribe();
    }, [history, location.pathname]);

    const loadOverview = useCallback(async () => {
        setOverviewError(null);
        try {
            const result = await firebase
                .functions()
                .httpsCallable("getDesktopAccessOverview")();
            setOverview(result.data);
        } catch (error) {
            setOverviewError(error.message || "Desktop app access couldn't be loaded");
        }
    }, []);

    useEffect(() => {
        if (isSystemManager) {
            loadOverview();
        }
    }, [isSystemManager, loadOverview]);

    const today = todayIsoDate();

    const licenceByUid = useMemo(
        () => latestLicenceByUid(overview ? overview.licences : []),
        [overview],
    );

    const expiryDate = overview ? overview.expiryDate : null;
    // Keys can be given a shorter life than the limit, never a longer one
    const grantExpiryDate = grantExpiry || expiryDate;
    const earliestExpiry = addDaysIso(today, 1);

    // Starts at the limit; a manager can bring it forward but not past it
    useEffect(() => {
        if (expiryDate) setGrantExpiry(expiryDate);
    }, [expiryDate]);
    const macsLimit = Math.max(1, (overview && overview.maxDevicesLimit) || 1);
    const macsChoices = Array.from({ length: macsLimit }, (unused, i) => i + 1);
    const expiryUsable = Boolean(expiryDate && expiryDate > today);

    const sortedUsers = useMemo(
        () =>
            (users || [])
                .map((user) => ({
                    ...user,
                    displayName: [user.firstName, user.lastName]
                        .map((part) => (part || "").trim())
                        .filter(Boolean)
                        .join(" "),
                }))
                .filter((user) => user.displayName)
                .sort((a, b) => a.displayName.localeCompare(b.displayName)),
        [users],
    );

    const usersByKey = useMemo(() => {
        const byKey = new Map();
        sortedUsers.forEach((user) => byKey.set(user.key, user));
        return byKey;
    }, [sortedUsers]);

    const hubTree = useMemo(
        () => buildHubTree(folders, groups),
        [folders, groups],
    );

    // Search results for groups: a flat list, each with its hub path
    const sortedGroups = useMemo(
        () =>
            (groups || [])
                .map((group) => ({
                    ...group,
                    hubPath: hubTree.pathNames(group.hub).join(" › "),
                }))
                .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        [groups, hubTree],
    );

    // In the same order as the People list; anyone without a user record is
    // still listed, so the count matches the group's own
    const viewingGroupMembers = useMemo(() => {
        if (!viewingGroup) return [];
        const memberKeys = new Set(
            (viewingGroup.users || []).map((member) => member.key),
        );
        const members = sortedUsers
            .filter((user) => memberKeys.has(user.key))
            .map((user) => ({ ...user, fullName: user.displayName }));
        memberKeys.forEach((key) => {
            if (!usersByKey.has(key)) {
                members.push({ key: key, fullName: "User unavailable" });
            }
        });
        return members;
    }, [viewingGroup, sortedUsers, usersByKey]);

    const visibleUsers = useMemo(
        () =>
            sortedUsers.filter((user) =>
                matchesFilter(
                    filter,
                    user.displayName,
                    user.customRoleName || user.role,
                ),
            ),
        [sortedUsers, filter],
    );

    const visibleGroups = useMemo(
        () =>
            sortedGroups.filter((group) =>
                matchesFilter(filter, group.name, group.hubPath),
            ),
        [sortedGroups, filter],
    );

    const selection = useMemo(
        () =>
            resolveRecipients({
                userKeys: Array.from(selectedUsers),
                groupKeys: Array.from(selectedGroups),
                groups: sortedGroups,
                knownUserKeys: Array.from(usersByKey.keys()),
                licenceByUid,
                webExpiryDate: expiryUsable ? grantExpiryDate : null,
                today,
            }),
        [
            selectedUsers,
            selectedGroups,
            sortedGroups,
            usersByKey,
            licenceByUid,
            expiryUsable,
            grantExpiryDate,
            today,
        ],
    );

    // Everyone with an older key than the current website date
    const renewals = useMemo(
        () =>
            expiryUsable
                ? renewalCandidates(
                      licenceByUid,
                      expiryDate,
                      Array.from(usersByKey.keys()),
                  )
                : [],
        [expiryUsable, licenceByUid, expiryDate, usersByKey],
    );

    const accessRows = useMemo(() => {
        return Array.from(licenceByUid.values())
            .map((licence) => {
                const user = usersByKey.get(licence.uid);
                return {
                    ...licence,
                    user: user,
                    displayName: user ? user.displayName : "Unknown user",
                    status: licenceStatus(licence, today),
                };
            })
            .filter((row) => matchesFilter(filter, row.displayName))
            .sort(
                (a, b) =>
                    STATUS_ORDER[a.status.status] -
                        STATUS_ORDER[b.status.status] ||
                    a.displayName.localeCompare(b.displayName),
            );
    }, [licenceByUid, usersByKey, today, filter]);

    const statusCounts = useMemo(() => {
        const counts = { active: 0, activated: 0, grace: 0, expired: 0 };
        licenceByUid.forEach((licence) => {
            const status = licenceStatus(licence, today).status;
            counts[status]++;
            if (
                status === "active" &&
                activationLabel(licence) !== "Not activated yet"
            ) {
                counts.activated++;
            }
        });
        return counts;
    }, [licenceByUid, today]);

    function toggle(setter, key) {
        setter((previous) => {
            const next = new Set(previous);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }

    function clearSelection() {
        setSelectedUsers(new Set());
        setSelectedGroups(new Set());
    }

    async function handleConfirmGrant() {
        const pending = pendingGrant;
        setPendingGrant(null);
        setGranting(true);

        const totals = {
            issued: 0,
            renewed: 0,
            alreadyHadAccess: 0,
            emailFailed: 0,
            noEmail: 0,
        };

        try {
            const grant = firebase
                .functions()
                .httpsCallable("grantDesktopAccess");

            for (let i = 0; i < pending.uids.length; i += GRANT_BATCH_SIZE) {
                // Renewals leave the number out: each person keeps at least
                // as many Macs as their previous key
                const result = await grant({
                    uids: pending.uids.slice(i, i + GRANT_BATCH_SIZE),
                    ...(pending.maxDevices
                        ? { maxDevices: pending.maxDevices }
                        : {}),
                    // Renewals leave the date out too, so they run to the limit
                    ...(pending.expiryDate
                        ? { expiryDate: pending.expiryDate }
                        : {}),
                });
                Object.keys(totals).forEach((key) => {
                    totals[key] += result.data[key] || 0;
                });
            }

            const summary = describeGrantResult(totals);
            enqueueSnackbar(summary.message, { variant: summary.variant });
            if (pending.kind === "grant") clearSelection();
        } catch (error) {
            // Earlier batches may have gone through; the reload shows where it got to
            enqueueSnackbar(error.message || "Access couldn't be granted", {
                variant: "error",
            });
        } finally {
            setGranting(false);
            loadOverview();
        }
    }

    async function handleSaveMacs() {
        const edit = macsEdit;
        setSavingMacs(true);
        try {
            const result = await firebase
                .functions()
                .httpsCallable("setDesktopAccessMacs")({
                uid: edit.row.uid,
                maxDevices: edit.maxDevices,
            });
            enqueueSnackbar(
                edit.row.displayName +
                    " can now use Nodiant Coder on " +
                    macsLabel(result.data.maxDevices) +
                    ".",
                { variant: "success" },
            );
            setMacsEdit(null);
            loadOverview();
        } catch (error) {
            enqueueSnackbar(error.message || "The number of Macs couldn't be changed", {
                variant: "error",
            });
        } finally {
            setSavingMacs(false);
        }
    }

    async function handleConfirmRemove() {
        const row = removeTarget;
        setRemoving(true);
        try {
            await firebase
                .functions()
                .httpsCallable("removeDesktopAccess")({ uid: row.uid });
            enqueueSnackbar(
                row.displayName + "'s Nodiant Coder key has been removed.",
                { variant: "success" },
            );
            setRemoveTarget(null);
            loadOverview();
        } catch (error) {
            enqueueSnackbar(error.message || "Their access couldn't be removed", {
                variant: "error",
            });
        } finally {
            setRemoving(false);
        }
    }

    function renderUserRow(user) {
        const licence = licenceByUid.get(user.key);
        // With a date set, only people already on it are done; anyone on an
        // older key can be ticked to get their next one
        const hasAccess = expiryUsable
            ? hasKeyForCurrentPeriod(licence, grantExpiryDate)
            : licenceIsCurrent(licence, today);
        const hasOlderKey = Boolean(licence) && !hasAccess;
        const checked = hasAccess || selectedUsers.has(user.key);

        return (
            <ListItemButton
                disabled={hasAccess}
                onClick={() => toggle(setSelectedUsers, user.key)}
                sx={{ height: ROW_HEIGHT, px: { xs: 1.5, sm: 2.5 } }}
            >
                <Checkbox
                    edge="start"
                    checked={checked}
                    disabled={hasAccess}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ "aria-label": "Select " + user.displayName }}
                />
                <ListItemAvatar>
                    <UserAvatar userOrHub={user} path="users" />
                </ListItemAvatar>
                <ListItemText
                    primary={user.displayName}
                    secondary={user.customRoleName || user.role}
                    primaryTypographyProps={{ noWrap: true }}
                    secondaryTypographyProps={{ noWrap: true }}
                />
                {hasAccess && (
                    <Chip
                        size="small"
                        label={"Until " + formatLicenceDate(licence.expiryDate)}
                        sx={{ ml: 1, flexShrink: 0 }}
                    />
                )}
                {hasOlderKey && (
                    <Chip
                        size="small"
                        variant="outlined"
                        label={
                            (licenceIsCurrent(licence, today)
                                ? "Key ends "
                                : "Key ended ") +
                            formatLicenceDate(licence.expiryDate)
                        }
                        sx={{ ml: 1, flexShrink: 0 }}
                    />
                )}
            </ListItemButton>
        );
    }

    function renderGroupSearchRow(group) {
        return (
            <Box sx={{ px: { xs: 1, sm: 1.75 } }}>
                <GroupRow
                    group={group}
                    detail={group.hubPath}
                    checked={selectedGroups.has(group.key)}
                    onToggle={() => toggle(setSelectedGroups, group.key)}
                    onOpen={() => setViewingGroup(group)}
                />
            </Box>
        );
    }

    function renderAccessRow(row) {
        return (
            <Box
                sx={{
                    height: ROW_HEIGHT,
                    display: "flex",
                    alignItems: "center",
                    px: { xs: 1.5, sm: 2.5 },
                    gap: 1,
                }}
            >
                <ListItemAvatar>
                    <UserAvatar userOrHub={row.user} path="users" />
                </ListItemAvatar>
                <ListItemText
                    primary={row.displayName}
                    secondary={
                        row.status.status === "active"
                            ? "Valid until " + formatLicenceDate(row.expiryDate)
                            : "Expired " +
                              formatLicenceDate(row.expiryDate) +
                              " · " +
                              (activationLabel(row) === "Not activated yet"
                                  ? "Never activated"
                                  : activationLabel(row))
                    }
                    primaryTypographyProps={{ noWrap: true }}
                    secondaryTypographyProps={{ noWrap: true }}
                />
                <Chip
                    size="small"
                    {...statusChipProps(row.status, row)}
                    sx={{ flexShrink: 0 }}
                />
                {row.status.status === "active" && (
                    <Button
                        size="small"
                        onClick={() =>
                            setMacsEdit({ row: row, maxDevices: row.maxDevices || 1 })
                        }
                        sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                    >
                        {macsLabel(row.maxDevices)}
                    </Button>
                )}
                <Tooltip title="Remove key">
                    <IconButton
                        size="small"
                        aria-label={"Remove " + row.displayName + "'s key"}
                        onClick={() => setRemoveTarget(row)}
                        sx={{ flexShrink: 0, color: "text.secondary" }}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        );
    }

    const searchField = (
        <TextField
            label={
                tab === "access"
                    ? "Search people"
                    : mode === "people"
                      ? "Search people"
                      : "Search groups"
            }
            size="small"
            fullWidth
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: "text.secondary" }} />
                        </InputAdornment>
                    ),
                    endAdornment: filter ? (
                        <InputAdornment position="end">
                            <IconButton
                                edge="end"
                                size="small"
                                aria-label="Clear search"
                                onClick={() => setFilter("")}
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ) : null,
                },
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />
    );

    const loading = !overview && !overviewError;
    const selectedCount = selectedUsers.size + selectedGroups.size;

    function renderBody() {
        if (overviewError) {
            return (
                <Box sx={{ p: 3 }}>
                    <Typography color="error" sx={{ mb: 2 }}>
                        {overviewError}
                    </Typography>
                    <Button variant="outlined" onClick={loadOverview}>
                        Try again
                    </Button>
                </Box>
            );
        }

        if (loading || !usersLoaded) {
            return (
                <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
                    <CircularProgress size={32} aria-label="Loading" />
                </Box>
            );
        }

        return (
            <>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        mx: { xs: 2, sm: 2.5 },
                        mt: 1.5,
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        color: "text.secondary",
                        border: "1px solid",
                        borderColor: expiryUsable ? primaryTintBorder : warningBorder,
                        backgroundColor: expiryUsable ? primarySurface : warningSurface,
                    }}
                >
                    {expiryUsable ? (
                        <EventOutlinedIcon
                            fontSize="small"
                            sx={{ mt: 0.15, flexShrink: 0, color: "primary.main" }}
                        />
                    ) : (
                        <WarningAmberOutlinedIcon
                            fontSize="small"
                            sx={{ mt: 0.15, flexShrink: 0, color: "warning.main" }}
                        />
                    )}
                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                        {expiryUsable ? (
                            // The email and where to find the key again are
                            // spelled out in the confirmation before granting
                            <>
                                Keys last until{" "}
                                <strong>{formatLicenceDate(expiryDate)}</strong>
                                {macsLimit > 1 && (
                                    <>
                                        {" "}· up to{" "}
                                        <strong>{macsLabel(macsLimit)}</strong>{" "}
                                        each
                                    </>
                                )}
                                {" "}· sent by email with the download link
                            </>
                        ) : (
                            "Access can't be granted at the moment because the licence period hasn't been set. Please contact SportsViz."
                        )}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: { xs: "stretch", md: "center" },
                        gap: { xs: 0, md: 2 },
                        mt: 0.5,
                        pr: { md: 2.5 },
                    }}
                >
                <Tabs
                    value={tab}
                    onChange={(event, value) => {
                        setTab(value);
                        setFilter("");
                    }}
                    sx={{ px: { xs: 1, sm: 1.5 }, flexShrink: 0 }}
                >
                    <Tab value="grant" label="Grant access" />
                    <Tab
                        value="access"
                        label={"Access list (" + licenceByUid.size + ")"}
                    />
                </Tabs>

                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: "flex-end",
                        gap: 1.5,
                        px: { xs: 2, sm: 2.5, md: 0 },
                        py: { xs: 1.5, md: 1 },
                    }}
                >
                    {tab === "grant" && (
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={mode}
                            onChange={(event, value) => {
                                if (value) {
                                    setMode(value);
                                    setFilter("");
                                }
                            }}
                            sx={{ flexShrink: 0 }}
                        >
                            <ToggleButton value="people">People</ToggleButton>
                            <ToggleButton value="groups">Groups</ToggleButton>
                        </ToggleButtonGroup>
                    )}
                    <Box sx={{ flex: 1, maxWidth: { md: 480 } }}>
                        {searchField}
                    </Box>
                </Box>
                </Box>

                <Divider />

                {tab === "grant" ? (
                    <>
                        {mode === "people" ? (
                            <VirtualizedList
                                items={visibleUsers}
                                itemHeight={ROW_HEIGHT}
                                getItemKey={(item) => item.key}
                                renderItem={renderUserRow}
                                sx={listSx}
                            />
                        ) : filter ? (
                            visibleGroups.length === 0 ? (
                                <Typography
                                    sx={{
                                        height: LIST_HEIGHT,
                                        p: 3,
                                        color: "text.secondary",
                                        textAlign: "center",
                                    }}
                                >
                                    No groups match that search.
                                </Typography>
                            ) : (
                                <VirtualizedList
                                    items={visibleGroups}
                                    itemHeight={GROUP_ROW_HEIGHT}
                                    getItemKey={(item) => item.key}
                                    renderItem={renderGroupSearchRow}
                                    sx={listSx}
                                />
                            )
                        ) : (
                            <HubGroupNavigator
                                tree={hubTree}
                                selectedGroups={selectedGroups}
                                onToggleGroup={(key) =>
                                    toggle(setSelectedGroups, key)
                                }
                                onViewGroup={setViewingGroup}
                                height={LIST_HEIGHT}
                            />
                        )}
                        <Divider />
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: { xs: "stretch", sm: "center" },
                                flexDirection: { xs: "column", sm: "row" },
                                gap: 1.5,
                                p: { xs: 2, sm: 2.5 },
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ color: "text.secondary", flexGrow: 1 }}
                            >
                                {selectedCount === 0
                                    ? "Tick people or groups to give them access."
                                    : selection.recipients.length +
                                      (selection.recipients.length === 1
                                          ? " person"
                                          : " people") +
                                      " will get access" +
                                      (selection.alreadyHaveAccess > 0
                                          ? " · " +
                                            selection.alreadyHaveAccess +
                                            " already " +
                                            (selection.alreadyHaveAccess === 1
                                                ? "has"
                                                : "have") +
                                            " it"
                                          : "")}
                            </Typography>
                            {selectedCount > 0 && (
                                <Button onClick={clearSelection} disabled={granting}>
                                    Clear
                                </Button>
                            )}
                            <Button
                                variant="outlined"
                                color="inherit"
                                disabled={granting || !expiryUsable}
                                onClick={(event) => setExpiryAnchor(event.currentTarget)}
                                startIcon={<CalendarMonthOutlinedIcon />}
                                aria-label={
                                    "Expires " + formatLicenceDate(grantExpiryDate)
                                }
                                sx={{
                                    flexShrink: 0,
                                    textTransform: "none",
                                    whiteSpace: "nowrap",
                                    borderColor: "divider",
                                    color: "text.primary",
                                    fontWeight: 500,
                                    px: 1.75,
                                }}
                            >
                                {"Expires " + formatLicenceDate(grantExpiryDate)}
                                {grantExpiry &&
                                    expiryDate &&
                                    grantExpiry < expiryDate && (
                                        <Box
                                            component="span"
                                            sx={{ ml: 0.75, color: "text.secondary" }}
                                        >
                                            · shortened
                                        </Box>
                                    )}
                            </Button>
                            {macsLimit > 1 && (
                                <TextField
                                    select
                                    size="small"
                                    label="Macs per person"
                                    value={Math.min(grantMacs, macsLimit)}
                                    onChange={(event) =>
                                        setGrantMacs(Number(event.target.value))
                                    }
                                    disabled={granting}
                                    sx={{ minWidth: 150 }}
                                >
                                    {macsChoices.map((count) => (
                                        <MenuItem key={count} value={count}>
                                            {macsLabel(count)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                            <Button
                                variant="contained"
                                disableElevation
                                disabled={
                                    !expiryUsable ||
                                    granting ||
                                    selection.recipients.length === 0
                                }
                                onClick={() =>
                                    setPendingGrant({
                                        kind: "grant",
                                        uids: selection.recipients,
                                        alreadyHaveAccess:
                                            selection.alreadyHaveAccess,
                                        maxDevices: Math.min(grantMacs, macsLimit),
                                        expiryDate: grantExpiryDate,
                                    })
                                }
                                startIcon={
                                    granting ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : null
                                }
                                sx={{ fontWeight: 700, px: 2.25 }}
                            >
                                {granting ? "Granting…" : "Grant access"}
                            </Button>
                        </Box>
                    </>
                ) : (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: { xs: "stretch", sm: "center" },
                                flexDirection: { xs: "column", sm: "row" },
                                gap: 1.5,
                                px: { xs: 2, sm: 2.5 },
                                py: 1.5,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ color: "text.secondary", flexGrow: 1 }}
                            >
                                {statusCounts.active} valid (
                                {statusCounts.activated} activated) ·{" "}
                                {statusCounts.grace} in grace period ·{" "}
                                {statusCounts.expired} expired
                                {renewals.length > 0 &&
                                    " · " +
                                        renewals.length +
                                        (renewals.length === 1
                                            ? " person needs"
                                            : " people need") +
                                        " a key for " +
                                        formatLicenceDate(expiryDate)}
                            </Typography>
                            {renewals.length > 0 && (
                                <Button
                                    variant="contained"
                                    disableElevation
                                    disabled={granting}
                                    onClick={() =>
                                        setPendingGrant({
                                            kind: "renew",
                                            uids: renewals,
                                            alreadyHaveAccess: 0,
                                        })
                                    }
                                    sx={{ fontWeight: 700, px: 2.25 }}
                                >
                                    {granting
                                        ? "Sending…"
                                        : "Send new keys to everyone"}
                                </Button>
                            )}
                        </Box>
                        <Divider />
                        {renderAccessList()}
                    </>
                )}
            </>
        );
    }

    function renderAccessList() {
        return accessRows.length === 0 ? (
                    <Typography
                        sx={{ p: 3, color: "text.secondary", textAlign: "center" }}
                    >
                        {filter
                            ? "No one matches that search."
                            : "No one has been given access yet."}
                    </Typography>
                ) : (
                    <VirtualizedList
                        items={accessRows}
                        itemHeight={ROW_HEIGHT}
                        getItemKey={(row) => row.id}
                        renderItem={renderAccessRow}
                        sx={listSx}
                    />
                );
    }

    return (
        <>
            {isSystemManager === true && (
                <DesktopAppPageFrame
                    maxWidth={false}
                    compact
                    title="Desktop App"
                    subtitle="Give people access to Nodiant Coder, the desktop app for Mac."
                >
                    {renderBody()}
                </DesktopAppPageFrame>
            )}
            {isSystemManager === false && <NoPermissionsMessage />}
            {isSystemManager === undefined && (
                <Box
                    sx={{
                        minHeight: "calc(100vh - 64px)",
                        display: "grid",
                        placeItems: "center",
                        backgroundColor: "background.default",
                    }}
                >
                    <CircularProgress size={32} aria-label="Loading" />
                </Box>
            )}

            <Dialog open={Boolean(pendingGrant)} onClose={() => setPendingGrant(null)}>
                {pendingGrant && (
                    <>
                        <DialogTitle>
                            {(pendingGrant.kind === "renew"
                                ? "Send new keys to "
                                : "Grant access to ") +
                                pendingGrant.uids.length +
                                (pendingGrant.uids.length === 1
                                    ? " person?"
                                    : " people?")}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText
                                sx={{ mb: pendingGrant.alreadyHaveAccess ? 1.5 : 0 }}
                            >
                                {pendingGrant.kind === "renew"
                                    ? "Everyone on the access list without a key for " +
                                      formatLicenceDate(expiryDate) +
                                      " gets a new key valid until then, for the same number of Macs as before, by email. Their current keys keep working until they expire, and people whose keys have already expired get access again."
                                    : "Each person gets a Nodiant Coder activation key valid until " +
                                      formatLicenceDate(pendingGrant.expiryDate) +
                                      " for " +
                                      macsLabel(pendingGrant.maxDevices) +
                                      ", sent by email with a download link. They can also find it under Desktop App in the Nodiant Online menu. Anyone with an older key gets their next one."}
                            </DialogContentText>
                            {pendingGrant.alreadyHaveAccess > 0 && (
                                <DialogContentText>
                                    {pendingGrant.alreadyHaveAccess +
                                        (pendingGrant.alreadyHaveAccess === 1
                                            ? " person already has a key for this date and will be skipped."
                                            : " people already have a key for this date and will be skipped.")}
                                </DialogContentText>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setPendingGrant(null)}>Cancel</Button>
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={handleConfirmGrant}
                            >
                                {pendingGrant.kind === "renew"
                                    ? "Send new keys"
                                    : "Grant access"}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Popover
                open={Boolean(expiryAnchor)}
                anchorEl={expiryAnchor}
                onClose={() => setExpiryAnchor(null)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                slotProps={{ paper: { sx: { p: 1.5, borderRadius: 3, width: 320 } } }}
            >
                <Typography variant="body2" sx={{ px: 0.5, pb: 1, color: "text.secondary" }}>
                    Keys can run until{" "}
                    <strong>{formatLicenceDate(expiryDate)}</strong>. Pick an
                    earlier date for a shorter licence.
                </Typography>
                <NodiantCalendar
                    selectedDate={toLocalDate(grantExpiryDate)}
                    minDate={toLocalDate(earliestExpiry)}
                    maxDate={toLocalDate(expiryDate)}
                    onDateChange={(date) => {
                        setGrantExpiry(toIsoDate(date));
                        setExpiryAnchor(null);
                    }}
                />
                {grantExpiry && expiryDate && grantExpiry < expiryDate && (
                    <Button
                        fullWidth
                        size="small"
                        onClick={() => {
                            setGrantExpiry(expiryDate);
                            setExpiryAnchor(null);
                        }}
                        sx={{ mt: 1 }}
                    >
                        Reset to {formatLicenceDate(expiryDate)}
                    </Button>
                )}
            </Popover>

            <Dialog open={Boolean(macsEdit)} onClose={() => setMacsEdit(null)}>
                {macsEdit && (
                    <>
                        <DialogTitle>
                            {"Macs for " + macsEdit.row.displayName}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText sx={{ mb: 2 }}>
                                {"Their current key is activated on " +
                                    (macsEdit.row.activationCount || 0) +
                                    " of " +
                                    macsLabel(macsEdit.row.maxDevices) +
                                    ". The key stays the same; if you add Macs they're emailed to say they can enter it on another Mac."}
                            </DialogContentText>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Macs"
                                value={macsEdit.maxDevices}
                                onChange={(event) =>
                                    setMacsEdit({
                                        ...macsEdit,
                                        maxDevices: Number(event.target.value),
                                    })
                                }
                            >
                                {macsChoices.map((count) => (
                                    <MenuItem
                                        key={count}
                                        value={count}
                                        disabled={
                                            count < (macsEdit.row.activationCount || 0)
                                        }
                                    >
                                        {macsLabel(count)}
                                    </MenuItem>
                                ))}
                                {macsEdit.maxDevices > macsLimit && (
                                    <MenuItem value={macsEdit.maxDevices} disabled>
                                        {macsLabel(macsEdit.maxDevices)} (over the current limit)
                                    </MenuItem>
                                )}
                            </TextField>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setMacsEdit(null)}>Cancel</Button>
                            <Button
                                variant="contained"
                                disableElevation
                                disabled={
                                    savingMacs ||
                                    macsEdit.maxDevices === (macsEdit.row.maxDevices || 1) ||
                                    macsEdit.maxDevices > macsLimit
                                }
                                onClick={handleSaveMacs}
                            >
                                {savingMacs ? "Saving…" : "Save"}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Dialog open={Boolean(removeTarget)} onClose={() => setRemoveTarget(null)}>
                {removeTarget && (
                    <>
                        <DialogTitle>
                            {"Remove " + removeTarget.displayName + "'s key?"}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText sx={{ mb: 1.5 }}>
                                Their key will be deleted and can't be activated
                                on any more Macs. They'll disappear from this list
                                and won't be told.
                            </DialogContentText>
                            <DialogContentText
                                sx={{ color: "warning.main", fontWeight: 600 }}
                            >
                                {(removeTarget.activationCount ||
                                    (removeTarget.redeemed ? 1 : 0)) > 0
                                    ? "Nodiant Coder will keep working on the " +
                                      macsLabel(
                                          removeTarget.activationCount ||
                                              (removeTarget.redeemed ? 1 : 0),
                                      ) +
                                      " it's already activated on until " +
                                      formatLicenceDate(removeTarget.expiryDate) +
                                      " (plus a " +
                                      GRACE_DAYS +
                                      "-day grace period). The app works offline and doesn't check back, so removing the key can't stop it there."
                                    : "It hasn't been activated on any Mac yet, so nothing is left running."}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setRemoveTarget(null)}>Cancel</Button>
                            <Button
                                color="error"
                                variant="contained"
                                disableElevation
                                disabled={removing}
                                onClick={handleConfirmRemove}
                            >
                                {removing ? "Removing…" : "Remove key"}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <GroupMembersDialog
                open={Boolean(viewingGroup)}
                groupName={viewingGroup ? viewingGroup.name : ""}
                breadcrumb={
                    viewingGroup ? hubTree.pathNames(viewingGroup.hub).join(" › ") : ""
                }
                members={viewingGroupMembers}
                onClose={() => setViewingGroup(null)}
            />

            <ToolbarWithLogo />
        </>
    );
}
